/**
 * 3-D pane for the Control Surfaces workbench (ControlSurfacesSurface).
 *
 * Renders three things and stays deliberately dumb - the parent computes all
 * the geometry inputs:
 *   a. the TESSELLATED AIRCRAFT (POST /geometry/tessellate) as one indexed
 *      BufferGeometry, uploaded ONCE per mesh prop and never rebuilt on param
 *      changes (meshes run to ~400k triangles);
 *   b. the CONTROL-SURFACE SILHOUETTE lofted from the live per-station cs0
 *      contours - the parent resamples each station's cs_contour_0 to a fixed
 *      ring and maps it to 3D by span axis; this component turns consecutive
 *      rings into quad strips + end caps (the studio loftCS), drawn amber with
 *      polygonOffset so it reads over the skin at 0 deflection;
 *   c. the HINGE LINE, dashed, between the band-edge engine hinges.
 *
 * The silhouette sits in a pivot group on the hinge line and rotates about the
 * hinge axis by deflectionDeg - the sign matches the 2D section panel (the
 * engine's positive deflection = TE toward negative section-vert; see the
 * derivation at the deflection effect below).
 *
 * Everything (aircraft + silhouette pivot + hinge) lives in ONE content group.
 * `upSign` (the segmenter's aircraft z-orientation) rotates that group PI about
 * the X axis when -1, so a z-DOWN STEP (e.g. the C172) renders upright. This is
 * a PROPER rotation (a 180-degree roll of the whole model), so the deflection
 * stays self-consistent with the mirrored 2D section panel - no per-axis sign
 * fix-up is needed (see the deflection effect). Camera fits transform the
 * geometry boxes through the group matrix so the refit tracks the flip.
 *
 * three.js lifecycle mirrors AircraftViewer3D: one-time scene setup, raf loop
 * with free-tumbling TrackballControls (no gimbal pole lock - aircraft get
 * inspected from below and over-the-nose), ResizeObserver, full disposal. Colours
 * come off the theme-aware highlift palette (V5 tokens), no hardcoded hexes.
 */

import { useEffect, useMemo, useRef, type CSSProperties } from "react";
import * as THREE from "three";
import { TrackballControls } from "three/examples/jsm/controls/TrackballControls.js";
import { toCreasedNormals } from "three/examples/jsm/utils/BufferGeometryUtils.js";

import type { TessMesh } from "../../api/controlSurface";
import type { HighliftPalette } from "../highlift/foil/useHighliftTheme";

/** One lofted station ring: the cs0 contour resampled + mapped to 3D [x, y, z]. */
export type CsLoftRing = [number, number, number][];

/** Everything the silhouette needs, computed by the parent on the debounce path. */
export interface CsLoft {
  /** Station rings inboard -> outboard, all the same point count. */
  rings: CsLoftRing[];
  /** Band-edge engine hinge points in 3D, [at eta1, at eta2]. */
  hinge: [[number, number, number], [number, number, number]];
  /** The surface's span axis - decides the deflection sign about the hinge. */
  spanAxis: "y" | "z";
}

export interface ControlSurfaceViewer3DProps {
  palette: HighliftPalette;
  /** Tessellated aircraft, or null while loading / after a tess failure. */
  mesh: TessMesh | null;
  /** Silhouette loft, or null when the engine has no valid section across the band. */
  loft: CsLoft | null;
  deflectionDeg: number;
  /**
   * Aircraft z-orientation from the segmenter (+1 z-up, -1 z-down). DISPLAY-ONLY: when -1 the
   * whole content group (aircraft + pivot + hinge) is rotated PI about X so a z-DOWN STEP
   * renders upright. Geometry stays in the model frame.
   */
  upSign?: 1 | -1;
  /**
   * Camera-fit identity. WHY: the default (undefined) refits the camera on every new `mesh` -
   * right for source switches, WRONG for the nTop frame scrubber, which swaps the WHOLE mesh
   * per slider tick (each deflection frame is its own STL) and must keep the camera dead still
   * so the swap reads as the surface moving, not the view. When provided, the camera refits
   * ONLY when this key changes (e.g. the notebook key): frame swaps under the same key keep
   * the current framing. Undefined preserves the legacy fit-per-mesh behavior exactly.
   */
  fitKey?: string;
}

/**
 * BufferGeometry from the tessellate response, with crease-aware normals (built
 * indexed, then returned NON-indexed by toCreasedNormals). Computed once per mesh.
 */
function meshGeometry(mesh: TessMesh): THREE.BufferGeometry {
  const pos = new Float32Array(mesh.v.length * 3);
  for (let i = 0; i < mesh.v.length; i++) {
    const p = mesh.v[i]!;
    pos[i * 3] = p[0];
    pos[i * 3 + 1] = p[1];
    pos[i * 3 + 2] = p[2];
  }
  const idx = new Uint32Array(mesh.t.length * 3);
  for (let i = 0; i < mesh.t.length; i++) {
    const t = mesh.t[i]!;
    idx[i * 3] = t[0];
    idx[i * 3 + 1] = t[1];
    idx[i * 3 + 2] = t[2];
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  g.setIndex(new THREE.BufferAttribute(idx, 1));
  // Crease-aware normals. Baked STLs are fully welded, so one shared vertex ring
  // runs from the outer skin onto the 90-degree cove wall; plain
  // computeVertexNormals() area-averages across that crease and smears a
  // bright/dark band around the aileron slot (and fan-wrinkles the boolean
  // slivers). toCreasedNormals splits the normal at any edge whose dihedral
  // exceeds 30 degrees - crisp cove walls / TE / slot rim, smooth skin - and as
  // a bonus welds-then-smooths the STEP path's duplicated face-border verts,
  // removing its shading seams. It de-indexes internally and RETURNS A
  // NON-INDEXED geometry (positions triple, no index); the Mesh render path and
  // computeBoundingBox handle that fine. loftGeometry (the silhouette) is left
  // on plain flat-facet normals.
  return toCreasedNormals(g, THREE.MathUtils.degToRad(30));
}

/**
 * Loft consecutive station rings into quad strips + centroid-fan end caps
 * (port of the studio's loftCS). Positions are shifted by -pivot so the
 * geometry can live inside a pivot group positioned on the hinge line.
 * Non-indexed triangle soup - computeVertexNormals gives crisp facets.
 */
function loftGeometry(rings: CsLoftRing[], pivot: [number, number, number]): THREE.BufferGeometry {
  const tris: number[] = [];
  const push = (p: [number, number, number]) => {
    tris.push(p[0] - pivot[0], p[1] - pivot[1], p[2] - pivot[2]);
  };
  for (let i = 0; i < rings.length - 1; i++) {
    const r0 = rings[i]!;
    const r1 = rings[i + 1]!;
    const n = Math.min(r0.length, r1.length);
    for (let j = 0; j < n - 1; j++) {
      push(r0[j]!);
      push(r1[j]!);
      push(r1[j + 1]!);
      push(r0[j]!);
      push(r1[j + 1]!);
      push(r0[j + 1]!);
    }
  }
  const cap = (ring: CsLoftRing, flip: boolean) => {
    const c: [number, number, number] = [0, 0, 0];
    for (const p of ring) {
      c[0] += p[0];
      c[1] += p[1];
      c[2] += p[2];
    }
    c[0] /= ring.length;
    c[1] /= ring.length;
    c[2] /= ring.length;
    for (let j = 0; j < ring.length - 1; j++) {
      if (flip) {
        push(c);
        push(ring[j + 1]!);
        push(ring[j]!);
      } else {
        push(c);
        push(ring[j]!);
        push(ring[j + 1]!);
      }
    }
  };
  const first = rings[0];
  const last = rings[rings.length - 1];
  if (first) cap(first, true);
  if (last && rings.length > 1) cap(last, false);
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(tris), 3));
  g.computeVertexNormals();
  return g;
}

export function ControlSurfaceViewer3D({
  palette,
  mesh,
  loft,
  deflectionDeg,
  upSign = 1,
  fitKey,
}: ControlSurfaceViewer3DProps) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const controlsRef = useRef<TrackballControls | null>(null);
  const rafRef = useRef<number | null>(null);

  // ONE content group holds everything model-frame (aircraft, silhouette pivot, hinge) so the
  // upSign flip is a single group rotation rather than per-object sign surgery.
  const contentRef = useRef<THREE.Group | null>(null);
  const aircraftRef = useRef<THREE.Mesh | null>(null);
  const pivotRef = useRef<THREE.Group | null>(null);
  const silhouetteRef = useRef<THREE.Mesh | null>(null);
  const hingeRef = useRef<THREE.Line | null>(null);

  const aircraftMatRef = useRef<THREE.MeshStandardMaterial | null>(null);
  const silhouetteMatRef = useRef<THREE.MeshStandardMaterial | null>(null);
  const hingeMatRef = useRef<THREE.LineDashedMaterial | null>(null);

  // Camera fitted per aircraft mesh (or, failing that, once to the loft), then
  // left alone - the mesh prop only changes identity when the SOURCE changes
  // (per-source cache upstream), so param edits never yank the view around,
  // while a source switch (a mm-authored C172 vs a metres FA-18 is a 300x
  // scale jump that would strand the camera and its near/far) refits cleanly.
  const fittedRef = useRef<"none" | "loft" | "mesh">("none");
  // fitKey plumbing: the CURRENT key rides a ref (a key change always arrives
  // with a mesh change, so the mesh effect need not re-run on the key alone)
  // and the key last fitted is remembered to skip refits under the same key.
  const fitKeyRef = useRef(fitKey);
  fitKeyRef.current = fitKey;
  const lastFitKeyRef = useRef<string | undefined>(undefined);

  const paletteRef = useRef(palette);
  paletteRef.current = palette;

  const fitCamera = (box: THREE.Box3) => {
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    if (!camera || !controls || box.isEmpty()) return;
    const center = new THREE.Vector3();
    box.getCenter(center);
    const size = box.getSize(new THREE.Vector3()).length() || 10;
    controls.target.copy(center);
    const dir = new THREE.Vector3(-0.7, -0.9, 0.45).normalize();
    camera.position.copy(center.clone().add(dir.multiplyScalar(size * 1.1)));
    camera.near = size / 100;
    camera.far = size * 100;
    camera.updateProjectionMatrix();
    controls.update();
  };

  /** WORLD-space bounding box of the current content (aircraft first, else the silhouette),
   *  transformed through the content group so a camera fit tracks the upSign flip. An
   *  off-origin z-down aircraft (the C172's z runs -1714..-80) lands on the OPPOSITE side of
   *  the origin after the PI roll - fitting the raw geometry box would strand the camera. */
  const contentWorldBox = (): THREE.Box3 | null => {
    const content = contentRef.current;
    if (!content) return null;
    content.updateMatrixWorld(true);
    const aircraft = aircraftRef.current;
    if (aircraft) {
      if (!aircraft.geometry.boundingBox) aircraft.geometry.computeBoundingBox();
      if (aircraft.geometry.boundingBox) {
        return aircraft.geometry.boundingBox.clone().applyMatrix4(content.matrixWorld);
      }
    }
    const pivot = pivotRef.current;
    const silhouette = silhouetteRef.current;
    if (pivot && silhouette) {
      if (!silhouette.geometry.boundingBox) silhouette.geometry.computeBoundingBox();
      if (silhouette.geometry.boundingBox) {
        // Silhouette geometry is pivot-local (shifted by -pivot): translate back to content
        // space first, then through the group matrix.
        return silhouette.geometry.boundingBox
          .clone()
          .translate(pivot.position)
          .applyMatrix4(content.matrixWorld);
      }
    }
    return null;
  };

  // --- one-time scene setup (mirrors AircraftViewer3D) ---
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const w = mount.clientWidth || 640;
    const h = mount.clientHeight || 480;
    const pal = paletteRef.current;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h);
    renderer.setClearColor(new THREE.Color(pal.bg), 1);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    // All model-frame content hangs off this group; the upSign effect rotates it PI about X
    // for z-DOWN sources so the aircraft renders upright.
    const content = new THREE.Group();
    scene.add(content);
    contentRef.current = content;
    const camera = new THREE.PerspectiveCamera(45, w / h, 0.01, 5000);
    camera.up.set(0, 0, 1); // Z up (the segmenter's aircraft convention)
    camera.position.set(-18, -22, 10);

    // TrackballControls, not OrbitControls: an aircraft is inspected from below,
    // above and over-the-nose, and Orbit's pole clamp makes tilt fight azimuth
    // near straight-up/down ("cannot turn to the correct angles whilst tilting").
    // Trackball tumbles freely with no gimbal lock; the horizon is allowed to roll.
    const controls = new TrackballControls(camera, renderer.domElement);
    controls.rotateSpeed = 3.5;
    controls.zoomSpeed = 1.2;
    controls.panSpeed = 0.8;
    controls.dynamicDampingFactor = 0.15;

    scene.add(new THREE.AmbientLight(0xffffff, 0.7));
    const key = new THREE.DirectionalLight(0xffffff, 0.9);
    key.position.set(-1, -1, 2);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xffffff, 0.4);
    fill.position.set(1, 1, -1);
    scene.add(fill);

    // Materials are created once and recoloured on theme change - the aircraft
    // tone is the V5 primary pulled toward the surface so a whole airframe of
    // it stays calm; the silhouette is the amber accent with polygonOffset so
    // it reads over the skin at 0 deflection.
    aircraftMatRef.current = new THREE.MeshStandardMaterial({
      color: new THREE.Color(pal.primary).lerp(new THREE.Color(pal.surface2), 0.35),
      metalness: 0.1,
      roughness: 0.65,
      side: THREE.DoubleSide,
      flatShading: false,
    });
    silhouetteMatRef.current = new THREE.MeshStandardMaterial({
      color: new THREE.Color(pal.amber),
      metalness: 0.15,
      roughness: 0.5,
      side: THREE.DoubleSide,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    });
    hingeMatRef.current = new THREE.LineDashedMaterial({
      color: new THREE.Color(pal.flaperonHinge),
      dashSize: 1,
      gapSize: 0.6,
    });

    rendererRef.current = renderer;
    sceneRef.current = scene;
    cameraRef.current = camera;
    controlsRef.current = controls;

    const loop = () => {
      controls.update();
      renderer.render(scene, camera);
      rafRef.current = requestAnimationFrame(loop);
    };
    loop();

    const ro = new ResizeObserver(() => {
      const cw = mount.clientWidth;
      const ch = mount.clientHeight;
      if (cw && ch) {
        renderer.setSize(cw, ch);
        camera.aspect = cw / ch;
        camera.updateProjectionMatrix();
        controls.handleResize(); // Trackball caches the screen rect for its rotation math
      }
    });
    ro.observe(mount);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      controls.dispose();
      contentRef.current = null;
      aircraftRef.current?.geometry.dispose();
      silhouetteRef.current?.geometry.dispose();
      hingeRef.current?.geometry.dispose();
      aircraftMatRef.current?.dispose();
      silhouetteMatRef.current?.dispose();
      hingeMatRef.current?.dispose();
      renderer.dispose();
      // The Planform | 3D toggle remounts this component - release the WebGL
      // context too, or repeated toggles pile up live contexts until the
      // browser force-loses the oldest (possibly another screen's viewer).
      renderer.forceContextLoss();
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- theme: recolour in place, never rebuild geometry ---
  useEffect(() => {
    rendererRef.current?.setClearColor(new THREE.Color(palette.bg), 1);
    aircraftMatRef.current?.color
      .set(palette.primary)
      .lerp(new THREE.Color(palette.surface2), 0.35);
    silhouetteMatRef.current?.color.set(palette.amber);
    hingeMatRef.current?.color.set(palette.flaperonHinge);
  }, [palette]);

  // --- upSign: roll the WHOLE content group upright for z-DOWN sources ---
  // Rx(PI) is a PROPER rotation (a 180-degree roll of the model), not a mirror: the deflection
  // pivot + hinge axis ride inside the group, so the deflection stays physically consistent and
  // agrees with the (vert-mirrored) 2D section panel - no per-axis sign adjustment is needed.
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    const target = upSign === -1 ? Math.PI : 0;
    if (content.rotation.x === target) return;
    content.rotation.x = target;
    content.updateMatrixWorld(true);
    // The roll moves an off-origin model's centroid - refit whatever is showing.
    if (fittedRef.current !== "none") {
      const box = contentWorldBox();
      if (box) fitCamera(box);
    }
  }, [upSign]);

  // --- aircraft: uploaded once per mesh, NEVER rebuilt on param changes ---
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    if (aircraftRef.current) {
      content.remove(aircraftRef.current);
      aircraftRef.current.geometry.dispose();
      aircraftRef.current = null;
    }
    if (!mesh || mesh.v.length === 0 || mesh.t.length === 0 || !aircraftMatRef.current) {
      // Mesh cleared (source switched, new tess in flight or failed): release
      // the mesh fit so the next loft can rescue the view if no mesh arrives.
      if (fittedRef.current === "mesh") fittedRef.current = "none";
      return;
    }
    const geometry = meshGeometry(mesh);
    const obj = new THREE.Mesh(geometry, aircraftMatRef.current);
    content.add(obj);
    aircraftRef.current = obj;
    geometry.computeBoundingBox();
    if (geometry.boundingBox) {
      // fitKey semantics (see the prop doc): undefined = legacy fit-per-mesh;
      // a provided key refits ONLY when the key changes, so frame swaps under
      // the same key (the nTop scrubber) keep the camera dead still.
      const key = fitKeyRef.current;
      if (key === undefined || lastFitKeyRef.current !== key) {
        const box = contentWorldBox();
        if (box) {
          fitCamera(box);
          fittedRef.current = "mesh";
        }
        lastFitKeyRef.current = key;
      } else {
        fittedRef.current = "mesh"; // already framed for this key
      }
    }
  }, [mesh]);

  // --- silhouette + hinge line: the ONLY geometry rebuilt on param changes ---
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    if (pivotRef.current) {
      content.remove(pivotRef.current);
      silhouetteRef.current?.geometry.dispose();
      pivotRef.current = null;
      silhouetteRef.current = null;
    }
    if (hingeRef.current) {
      content.remove(hingeRef.current);
      hingeRef.current.geometry.dispose();
      hingeRef.current = null;
    }
    if (!loft || loft.rings.length < 2 || !silhouetteMatRef.current || !hingeMatRef.current) return;

    const [pa, pb] = loft.hinge;
    // Pivot at the hinge midpoint (studio convention) - any point on the axis works.
    const mid: [number, number, number] = [
      (pa[0] + pb[0]) / 2,
      (pa[1] + pb[1]) / 2,
      (pa[2] + pb[2]) / 2,
    ];
    const pivot = new THREE.Group();
    pivot.position.set(mid[0], mid[1], mid[2]);
    const silhouette = new THREE.Mesh(loftGeometry(loft.rings, mid), silhouetteMatRef.current);
    pivot.add(silhouette);
    content.add(pivot);
    pivotRef.current = pivot;
    silhouetteRef.current = silhouette;

    const hingeLen = Math.hypot(pb[0] - pa[0], pb[1] - pa[1], pb[2] - pa[2]);
    hingeMatRef.current.dashSize = Math.max(hingeLen / 18, 1e-6);
    hingeMatRef.current.gapSize = Math.max(hingeLen / 30, 1e-6);
    hingeMatRef.current.needsUpdate = true;
    const hingeGeom = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(pa[0], pa[1], pa[2]),
      new THREE.Vector3(pb[0], pb[1], pb[2]),
    ]);
    const hinge = new THREE.Line(hingeGeom, hingeMatRef.current);
    hinge.computeLineDistances();
    content.add(hinge);
    hingeRef.current = hinge;

    // No aircraft yet (tess still loading / failed)? Fit to the silhouette once
    // so the pane is never a void; the mesh refit takes over when it arrives.
    if (fittedRef.current === "none") {
      silhouette.geometry.computeBoundingBox();
      const box = contentWorldBox();
      if (box) {
        fitCamera(box);
        fittedRef.current = "loft";
      }
    }
  }, [loft]);

  // --- deflection: rotate the pivot about the hinge axis ---
  useEffect(() => {
    const pivot = pivotRef.current;
    if (!pivot || !loft) return;
    const [pa, pb] = loft.hinge;
    const axis = new THREE.Vector3(pb[0] - pa[0], pb[1] - pa[1], pb[2] - pa[2]);
    if (axis.lengthSq() < 1e-12) return;
    axis.normalize();
    // Sign derivation, matching the 2D section panel exactly: the engine's
    // positive deflection rotates the section by -delta in the (x, vert) plane
    // (TE toward -vert). The hinge axis runs eta1 -> eta2, i.e. toward +span.
    //   span 'y': 3D = (x, span, vert); the (x, vert) plane normal x_hat x v_hat
    //             is -span, so -delta about -span = +delta about the axis.
    //   span 'z': 3D = (x, vert, span); the plane normal is +span, so the
    //             rotation is -delta about the axis.
    // upSign needs NO term here: the flip is a rotation of the whole CONTENT
    // group (this pivot and its axis included), so the deflection rides it and
    // stays consistent with the 2D panel's mirrored vert axis.
    const sign = loft.spanAxis === "y" ? 1 : -1;
    pivot.setRotationFromAxisAngle(axis, (sign * deflectionDeg * Math.PI) / 180);
  }, [deflectionDeg, loft]);

  const style = useMemo<CSSProperties>(
    () => ({ width: "100%", height: "100%", position: "relative" }),
    [],
  );

  return <div ref={mountRef} style={style} data-testid="cs-viewer-3d" />;
}
